import _ from 'lodash'
import { Observable, of } from 'rxjs'
import { first, switchMap } from 'rxjs/operators'

import { 
    SearchResponse, 
    SearchResult, 
    Item, 
    Wemi,
    DataStoreOptions,
    AssociatedMedias,
    SearchQuery,
} from '@skinsoft/types'
import { wemiSelectors } from '@skinsoft/selectors'


import { OfflineDataFetcher, ItemOfflineDataFetcher, ItemOfflineDataUpdateCacher } from '@skinsoft/offline'
import { API_ENDPOINTS } from '@skinsoft/network'
import { EntityDataStore } from '@skinsoft/entity-data-store'
import { searchResultSelectors } from '@skinsoft/search-data-store'
import { isWemi, WemiUtils } from '@skinsoft/wemi-utils'
import { ID_STATUS_CREATED_ON_SKIN_REPORTER } from '@skinsoft/utils'

declare const config
const ITEM_SEARCH_TYPES: string[] = config.item_search_types

const SEARCH_PAGE_SIZE: number = 1000

// @ts-ignore
export class ItemDataStore extends EntityDataStore<Item, any, SearchResult | Wemi> {
    public static getInstance() {
        if (!ItemDataStore.instance) {
            ItemDataStore.instance = new ItemDataStore();
        }

        return ItemDataStore.instance;
    }

    public static install(Vue) {
        Vue.prototype.$itemDataStore = ItemDataStore.getInstance();
    }

    public static getItemSearchResultId(itemSearchResult: SearchResult | Wemi) {
        if ("id" in itemSearchResult) {
            return searchResultSelectors.id(itemSearchResult);
        } else {
            return wemiSelectors.id(itemSearchResult);
        }
    }

    private static instance: ItemDataStore = null as any;

    constructor() {
        super('items', ITEM_SEARCH_TYPES)
    }

    public exists(id: Item['id']) {
        return this.fetchOne(id)
            .pipe(first())
            .toPromise();
    }

    public searchAll(searchQuery: SearchQuery, pathAndValueToCheck?: any, 
        searchEndpointOverride: string | null = null, options: DataStoreOptions = {}, onlyOnLocallyCreated: boolean = false, forceSearchTextId: boolean = false)
        : Observable<SearchResponse<SearchResult | Wemi>> {

        if(!searchQuery.pageSize) searchQuery.pageSize = 15
        if(!searchQuery.page) searchQuery.page = 0

        return super.searchAll(searchQuery, pathAndValueToCheck, searchEndpointOverride, options, onlyOnLocallyCreated, forceSearchTextId)
    }

    public mergeWemi(data): SearchResponse<SearchResult | Wemi> {
        return {...data, allData: _.mergeWith(data[1].allData, data[0], (item, wemi) => {
            if(item && isWemi(item)) return item

            const workTitles = wemi.work?.descriptionMuseumWorkSchema?.workTitles;

            return {
                ...wemi,
                item,
                work: {
                    ...wemi.work,
                    indexTitle: (workTitles && workTitles.length) ? (workTitles[0]?.title) : undefined
                }
            }
        })}
    }

    public update(id: string, body: Partial<Item>, endpointOverride?: string, ignoreCachedStatus: boolean = false): Observable<Item> {
        let entityType = body.type ?? body.entityType
        if(entityType) {
            entityType = _.lowerFirst(entityType)
        }
        const endpoint = endpointOverride ?? API_ENDPOINTS[entityType as string]
        
        return super.update(id, body, endpoint, ignoreCachedStatus)
    }

    public getWemiFromItem(itemId: string): Observable<Wemi> {
        if (OfflineDataFetcher.isTemporaryId(itemId)) {
            return WemiUtils.getOrCreateWemiFromItemOffline(itemId)
        }
        return WemiUtils.getWemiFromItem(itemId)
    }

    public create(createBody?: Partial<Item>, body?: Partial<Item>): Observable<Item> {
        const uri = '/physical-museum-items';
        if (window.navigator.onLine || !body) {
            return this.proxy.fetch(uri, createBody, 'POST', undefined, true).pipe(
                switchMap(response => {
                    if (!body) {
                        return of(response);
                    }
                    // In case of a locally created entity, replacing temporary id by the one from db
                    const id: string = _.get(body, "id", '')
                    if (id && OfflineDataFetcher.isTemporaryId(id)) {
                        Object.defineProperty(body, "id", { value: response.id })
                    }
                    _.set(body, 'managementData.entryStatus', ID_STATUS_CREATED_ON_SKIN_REPORTER)
                    return this.update(response.id, _.merge(response, body));
                }),
                switchMap(({ id }) => this.fetchOne(id)),
            );
        } else { // Offline mode
            // the '_' prefix of the id identifies the id as temporary in the cache keys
            return this.createOffline(createBody, body).pipe(
                switchMap(item => {
                    if(!(item as any).associatedMedias) {
                        const associatedMedias: AssociatedMedias = {
                            medias: [],
                        }
                        _.set(item as any, 'associatedMedias', associatedMedias)
                    }
                    return of(item as any as Item)
                })
            )
        }
    }

    public createOffline(createBody?: Partial<Item>, body?: Partial<Item>) {
        return super.createOffline(createBody, body).pipe(
            switchMap(item => {
                if(!(item as any).associatedMedias) {
                    const associatedMedias: AssociatedMedias = {
                        medias: [],
                    }
                    _.set(item as any, 'associatedMedias', associatedMedias)
                }
                this.offlineDataUpdateCacher.storeBeforePushing(item.id, item)
                return of(item)
            })
        )
    }

    public addImage2(itemId: string, image: { newMediaId: string }) {
        return this.proxy.fetch(`/physical-museum-items/addMedia/${itemId}`,{idMedia: image.newMediaId}, 'PUT')
    }
    public addImage(item: Item, image: { newMediaId: string }) {
        return this.proxy.fetch(`/physical-museum-items/addMedia/${item.id}`,{idMedia: image.newMediaId}, 'PUT')
    }

    protected initializeDataFetcher() {
        // @ts-ignore
        this.offlineDataFetcher = new ItemOfflineDataFetcher()
    }

    protected initializeDataPusher() {
        this.offlineDataUpdateCacher = new ItemOfflineDataUpdateCacher()
    }
}
